/**
 * Shared helpers for overlaying a stock-price line on analytics charts.
 *
 * The analytics charts use categorical / bucketed x-axes (daily, weekly,
 * monthly, or epoch buckets) rather than a continuous time axis, so a daily
 * price series must be aligned to each chart's existing rows. The convention
 * here is "last close at or before the end of the bucket" — i.e. the closing
 * price for the period — which also degenerates to the exact day for daily
 * buckets.
 */

// One fixed, high-contrast color used across every chart so the overlay is
// instantly recognizable. Amber is unused by the existing NPS / sentiment /
// post palettes.
export const STOCK_COLOR = '#FF6B00';
export const STOCK_DASH_ARRAY = [6, 4]; // Chart.js borderDash
export const STOCK_DASH_STRING = '6 4'; // Recharts strokeDasharray

// Benchmark (peers / S&P 500 / sector) line color for the premium overlay —
// blue, paired with the amber company line so the two are easy to tell apart.
export const BENCH_COLOR = '#1f77b4';

// Premium overlay modes, used to drive the overlay-mode selector. 'price' is
// the original absolute stock-price overlay; the rest hit useStockPremium.
export const PREMIUM_MODES = [
    { value: 'price', label: 'Price' },
    { value: 'peers', label: 'vs Peers' },
    { value: 'snp500', label: 'vs S&P 500' },
    { value: 'sector', label: 'vs Sector' },
];

// Human label for a premium payload's benchmark (e.g. "S&P 500",
// "Restaurants / Casual Dining peers", "CONSUMER CYCLICAL sector (XLY)").
export const benchmarkLabel = (payload) =>
    payload?.benchmark_name || payload?.group || payload?.benchmark || 'Benchmark';

export const formatStockPrice = (v) =>
    v == null || Number.isNaN(Number(v)) ? '' : `$${Number(v).toFixed(2)}`;

// Build an ascending [{ t: ms, close }] view of the raw series.
function toSorted(series) {
    return (series || [])
        .map((p) => ({ t: new Date(p.date).getTime(), close: Number(p.adjusted_close) }))
        .filter((p) => !Number.isNaN(p.t) && !Number.isNaN(p.close))
        .sort((a, b) => a.t - b.t);
}

// Rightmost close with t <= timeMs (binary search). null if none.
function closeAtOrBefore(sorted, timeMs) {
    let lo = 0;
    let hi = sorted.length - 1;
    let ans = null;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid].t <= timeMs) {
            ans = sorted[mid].close;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return ans;
}

// End-of-bucket timestamp (ms) for a 'yyyy-MM-dd' bucket start under a period.
function bucketEndMs(fullDate, bucketPeriod) {
    const d = new Date(`${fullDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return NaN;
    if (bucketPeriod === 'week') {
        d.setDate(d.getDate() + 6);
    } else if (bucketPeriod === 'month') {
        d.setMonth(d.getMonth() + 1);
        d.setDate(0); // last day of the original month
    }
    d.setHours(23, 59, 59, 999);
    return d.getTime();
}

/**
 * Merge a `stockPrice` field into Recharts rows that carry a `fullDate`
 * ('yyyy-MM-dd') key and are bucketed by `bucketPeriod` ('day' | 'week' |
 * 'month'). Returns the original rows unchanged when there is no price data.
 */
export function mergeStockIntoRows(rows, series, bucketPeriod = 'day') {
    const sorted = toSorted(series);
    if (!sorted.length || !rows || !rows.length) return rows;
    return rows.map((row) => ({
        ...row,
        stockPrice: closeAtOrBefore(sorted, bucketEndMs(row.fullDate, bucketPeriod)),
    }));
}

/**
 * Merge a `stockPrice` field into rows keyed by an epoch-ms timestamp (used by
 * the sentiment chart). Each row gets the last close before the *next* row's
 * bucket starts; the final row gets the latest available close.
 */
export function mergeStockByTimestamp(rows, series, tsKey = 'timestamp') {
    const sorted = toSorted(series);
    if (!sorted.length || !rows || !rows.length) return rows;
    return rows.map((row, i) => {
        const boundary = i < rows.length - 1 ? rows[i + 1][tsKey] - 1 : Number.MAX_SAFE_INTEGER;
        return { ...row, stockPrice: closeAtOrBefore(sorted, boundary) };
    });
}

/**
 * Price aligned to an array of 'YYYY-MM' month labels (Chart.js / NPS), one
 * value per label = the closing price for that month. Returns nulls when there
 * is no price data so the dataset length still matches the labels.
 */
export function pricesForMonthLabels(series, monthLabels) {
    const sorted = toSorted(series);
    if (!sorted.length) return (monthLabels || []).map(() => null);
    return (monthLabels || []).map((label) =>
        closeAtOrBefore(sorted, bucketEndMs(`${label}-01`, 'month'))
    );
}

/* ----------------------------- Premium overlay ---------------------------- */

// Ascending [{ t: ms, stock, bench, premium }] view of a premium payload's
// `data` ([{ date, stock, benchmark, premium? }]). `premium` is the backend's
// pre-computed % (from add_premium) when present, else null. Tolerates raw
// closes or rebased values.
function toSortedPremium(data) {
    return (data || [])
        .map((p) => ({
            t: new Date(p.date).getTime(),
            stock: Number(p.stock),
            bench: Number(p.benchmark),
            premium: p.premium == null ? null : Number(p.premium),
        }))
        .filter((p) => !Number.isNaN(p.t) && !Number.isNaN(p.stock) && !Number.isNaN(p.bench))
        .sort((a, b) => a.t - b.t);
}

// Rightmost point with t <= timeMs (binary search). null if none.
function pointAtOrBefore(sorted, timeMs) {
    let lo = 0;
    let hi = sorted.length - 1;
    let ans = null;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid].t <= timeMs) {
            ans = sorted[mid];
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return ans;
}

// Premium % for an aligned point `p` against baseline `base`.
//   - Backend mode: the server already baselined `premium` at the period start
//     (add_premium). With a period-aligned fetch that ≈ the visible window, so
//     plot it directly.
//   - Fallback (no backend `premium`): re-baseline the raw/rebased closes to the
//     first visible row so the line still starts at ~0% at the left edge.
function premiumAt(p, base) {
    if (p == null) return null;
    if (base.premium != null && p.premium != null) return p.premium;
    if (!base.stock || !base.bench) return null;
    return ((p.stock / base.stock) / (p.bench / base.bench) - 1) * 100;
}

// The series point to index the premium against: the close at/before the
// *visible window's start*, or the earliest available point if the series
// begins inside the window. Fixed regardless of bucket granularity, so
// daily/weekly/monthly all measure the premium from the same origin. (Indexing
// to the first row's bucket END instead would push the monthly baseline ~a
// month later than daily/weekly and flip the sign.)
function baselineFor(sorted, startMs) {
    return pointAtOrBefore(sorted, startMs) || sorted[0] || null;
}

/**
 * Merge a `premiumPct` field (company's % out/under-performance vs the
 * benchmark) onto chart rows that carry a `fullDate` key and are bucketed by
 * `bucketPeriod`. Returns the original rows unchanged when there is no premium
 * data. >0 = outperforming.
 */
export function mergePremiumIntoRows(rows, premiumData, bucketPeriod = 'day') {
    const sorted = toSortedPremium(premiumData);
    if (!sorted.length || !rows || !rows.length) return rows;

    const base = baselineFor(sorted, new Date(rows[0].fullDate).getTime());
    if (!base) return rows;

    return rows.map((row) => ({
        ...row,
        premiumPct: premiumAt(pointAtOrBefore(sorted, bucketEndMs(row.fullDate, bucketPeriod)), base),
    }));
}

/**
 * Premium variant of mergeStockByTimestamp — for rows keyed by an epoch-ms
 * timestamp (the sentiment / pricing chart). Adds `premiumPct` to each row.
 */
export function mergePremiumByTimestamp(rows, premiumData, tsKey = 'timestamp') {
    const sorted = toSortedPremium(premiumData);
    if (!sorted.length || !rows || !rows.length) return rows;

    const base = baselineFor(sorted, rows[0][tsKey]);
    if (!base) return rows;

    return rows.map((row, i) => {
        const boundary = i < rows.length - 1 ? rows[i + 1][tsKey] - 1 : Number.MAX_SAFE_INTEGER;
        return { ...row, premiumPct: premiumAt(pointAtOrBefore(sorted, boundary), base) };
    });
}

/**
 * Premium variant of pricesForMonthLabels — for Chart.js charts with an array
 * of 'YYYY-MM' labels (NPS). Returns one premium % per label (nulls where there
 * is no data so the dataset length still matches the labels).
 */
export function premiumForMonthLabels(premiumData, monthLabels) {
    const labels = monthLabels || [];
    const sorted = toSortedPremium(premiumData);
    if (!sorted.length || !labels.length) return labels.map(() => null);

    const base = baselineFor(sorted, new Date(`${labels[0]}-01`).getTime());
    if (!base) return labels.map(() => null);
    return labels.map((label) => premiumAt(pointAtOrBefore(sorted, bucketEndMs(`${label}-01`, 'month')), base));
}

// Map a trailing window length (in months) to the nearest covering premium
// endpoint period, so a backend (period-start-baselined) premium lines up with
// what the chart shows. Periods: 1m, 6m, 1y, 3y, 5y, 10y, all.
export function periodForMonths(months) {
    if (!months || months <= 1) return '1m';
    if (months <= 6) return '6m';
    if (months <= 12) return '1y';
    if (months <= 36) return '3y';
    if (months <= 60) return '5y';
    if (months <= 120) return '10y';
    return 'all';
}

export const formatIndexed = (v) =>
    v == null || Number.isNaN(Number(v)) ? '' : Number(v).toFixed(1);

export const formatPremiumPct = (v) =>
    v == null || Number.isNaN(Number(v)) ? '' : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
